import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToDoubleMap, wsScanToMap } from '../../pipes';
import { SecurityTypesUtils, type SecurityTypes } from '../../types';
import type { Security } from '../../types/Security';
import { EMPTY_ARRAY } from '../../utils/empty';
import { getCurrencyPairFromSecurity, shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export function useFutureIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const futuresObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToMap<Security, string, SecurityTypes['Future'], SecurityTypes['Future']>({
          getUniqueKey: security => security.Symbol,
          getInsertable: security => security,
          newMapEachUpdate: false,
          isRelevant: SecurityTypesUtils.Future.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        map(futuresBySymbol => [...futuresBySymbol.values()])
      ),
    [securityMemoryObs]
  );

  const futuresByCurrencyPairObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToDoubleMap<Security, string, string, SecurityTypes['Future'], SecurityTypes['Future']>({
          getKey1: getCurrencyPairFromSecurity,
          getKey2: security => security.Symbol,
          getInsertable: security => security,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: SecurityTypesUtils.Future.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [securityMemoryObs]
  );

  // No delta model for this guy yet, pretty bespoke...
  const futuresByMarketByCurrencyObs = useMemo(
    () => futuresObs.pipe(map(getFuturesByMarketByCurrencyPair)),
    [futuresObs]
  );

  const futures = useObservableValue(() => futuresObs, [futuresObs], EMPTY_ARRAY);
  const futuresByCurrencyPair = useObservableValue(
    () => futuresByCurrencyPairObs,
    [futuresByCurrencyPairObs],
    new Map()
  );
  const futuresByMarketByCurrency = useObservableValue(
    () => futuresByMarketByCurrencyObs,
    [futuresByMarketByCurrencyObs],
    new Map()
  );

  return useMemo(
    () => ({ futures, futuresByCurrencyPair, futuresByMarketByCurrency }),
    [futures, futuresByCurrencyPair, futuresByMarketByCurrency]
  );
}

export function getFuturesByMarketByCurrencyPair(futures: SecurityTypes['Future'][]) {
  const futuresByMarketByCurrencyPair = new Map<string, Map<string, Set<SecurityTypes['Future']>>>();

  for (const future of futures) {
    const currencyPair = getCurrencyPairFromSecurity(future);

    for (const market of future.Markets ?? []) {
      const futuresByMarket =
        futuresByMarketByCurrencyPair.get(currencyPair) ?? new Map<string, Set<SecurityTypes['Future']>>();
      futuresByMarketByCurrencyPair.set(currencyPair, futuresByMarket);
      const expirationSet = futuresByMarket.get(market);

      if (expirationSet) {
        expirationSet.add(future);
      } else {
        futuresByMarket.set(market, new Set<SecurityTypes['Future']>([future]));
      }
    }
  }

  return futuresByMarketByCurrencyPair;
}
